import React, { memo, useEffect, useState } from 'react';
import { Order, PointOfSale } from '@m4a3/api-client';
import { Typography, Tooltip, Menu, MenuItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { selectCells, selectIsOrderSelected, selectOverweightColorPairingsForOrder } from '../../../selectors/ordersSelector';
import { selectPointOfSales } from '../../../selectors/adminSelector';
import { useAppSelector } from 'hooks/redux';
import { areOrdersEqual, getShortIdForOrder } from '../../../utils/orderUtils';
import { setHoverOrder, setSelectedOrderId } from 'actions/orderActions';
import { newNotification } from '../../../actions/uiActions';
import { inactiveStatuses } from '../../../constants';
import { getStatusData } from '../../../services/order-service';
import { getLocationColor } from '../../../utils/locationColors';
import { usePopupState, bindMenu, bindTrigger, bindContextMenu } from 'material-ui-popup-state/hooks';
import { selectPads } from '../../../selectors/liveAircraftSelectors';
import { OrderCardContext } from './OrderCardContext';
import { SyntheticEvent } from 'react';
import { getAllActivePadsForOrder } from '../../../utils/landingPadUtils';
import { OrderCardStatusIndicator } from './OrderCardStatusIndicator';
import { PadSelectorBar } from './PadSelectorBar';
import { TimeUntilTakeoffBadge } from './TimeUntilTakeoffBadge';

const OrderCardComponent = (
  props: {
    order: Order,
    aircraftData?: any,
    landingZones?: any[],
    pads: any[],
    openSuggestModal?: () => void
    onClickShowSuggestedDropModalForOrder?: (orderId: string) => void,
    onClickInvalidateDropLocation?: (orderId: string, locationId: string) => void,
    onOpenRouteConfirmation?: (order: Order) => void,
    updateLandingZoneForOrder?: (orderId: string, landingZoneId: string) => void,
    onAcceptOrder?: (order: Order) => void,
  }) => {

  const dispatch = useDispatch();
  const cells = useSelector(selectCells);
  const pointOfSales = useSelector(selectPointOfSales);
  const linkedOrdersForOverweightOrders = useAppSelector(state => selectOverweightColorPairingsForOrder(state, props.order.orderShortId));
  const pads = useAppSelector(selectPads);
  const [pos, setPos] = useState<PointOfSale | null>(null);
  const [showPadUI, setShowPadUI] = useState(false);
  const padAnchorRef = React.useRef<HTMLDivElement>(null);

  const isSelected = useAppSelector(state =>
    selectIsOrderSelected(state, props.order)
  );

  const shouldShowOverweightOrderUI = (): boolean => {
    if (linkedOrdersForOverweightOrders?.linkedOverweightOrderIds && linkedOrdersForOverweightOrders?.linkedOverweightOrderIds.length > 0) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (pointOfSales) {
      setPos(pointOfSales?.find((pos) => pos.id === props.order.posId || parseInt(props.order?.externalProviderId) === pos?.fdStoreId));
    }
  }, [pointOfSales]);

  const selectOrder = () => {
    if (!props.order.eircode) {
      dispatch(newNotification({
        variant: 'error',
        message: 'BLOCKING ERROR: Order has no placeId - do not proceed with order'
      }));
    }

    dispatch(setSelectedOrderId(props.order.id));
  };

  const mouseEnterOrder = () => {
    if (props.order) {
      dispatch(setHoverOrder(props.order?.id));
    }
  };

  const mouseExitOrder = () => {
    dispatch(setHoverOrder(null));
  };

  const stop = (func: (event: SyntheticEvent<any>) => void) => (
    event: SyntheticEvent<any>
  ) => {
    func(event);
    event.stopPropagation();
  };

  const popupStateContextMenu = usePopupState({
    variant: 'popover',
    popupId: 'context-menu'
  });
  
  const popupStateAircraft = usePopupState({
    variant: 'popover',
    popupId: 'demo-aircraft'
  });
  
  const popupStateArrivalPad = usePopupState({
    variant: 'popover',
    popupId: 'arrival-pad'
  });

  // Helper function to determine if ArrivalPadTag should be enabled
  const shouldEnableArrivalPadTag = (): boolean => {
    // Enable if order is from JUSTEAT and selected landing pad is BT_A
    const isJustEatOrder = props.order.externalProviderName === 'JUSTEAT';
    
    if (!isJustEatOrder) {
      return false; // Keep disabled for all non-JUSTEAT orders
    }
    
    // For JUSTEAT orders, check if the selected landing pad is BT_A
    const selectedPadName = props.pads?.find(pad => pad.id === props.order.destinationLandingPadId)?.name;
    return selectedPadName === 'BT_A';
  };
  
  const getPadNameById = (padId: number | null): string => {
    if (!padId || padId === null) return '';
    const pad = pads.find(p => p.id == padId);
    return pad?.name || padId.toString();
  };

  const getDeparturePadButtonText = (): string => {
    if (!props.order.originLandingPadId) return 'Pad...';
    if (props.order.originLandingPadId === null) return 'None';
    return getPadNameById(props.order.originLandingPadId) || 'Pad...';
  };

  const getArrivalPadButtonText = (): string => {
    if (!props.order.destinationLandingPadId) return 'Destination';
    if (props.order.destinationLandingPadId === null) return 'None';
    return getPadNameById(props.order.destinationLandingPadId) || 'Destination';
  };

  // Mission status mapping
  const MISSION_STATUS = {
    PROPOSED: { text: 'Proposed', color: 'orange' },
    HOLD: { text: 'Hold', color: 'green' },
    IN_PROGRESS: { text: 'In Progress', color: 'red' },
    COMPLETED: { text: 'Complete', color: 'gray' },
    MANUAL: { text: 'Aborted', color: 'red' },
    ABORTED: { text: 'Aborted', color: 'red' }
  };
  
  const activeMissionStatus = (MISSION_STATUS as any)[
    props.order.mission?.status || (props.order as any).missionStatus
  ];
  
  return (
    <div
      onClick={selectOrder}
      onMouseEnter={mouseEnterOrder}
      onMouseLeave={mouseExitOrder}
      {...bindContextMenu(popupStateContextMenu)}
      style={{
        width: 262,
        backgroundColor: '#FFFFFF',
        border: isSelected ? '3px solid #0583FF' : '1px solid #d1d5db',
        borderRadius: 16,
        position: 'relative',
        cursor: 'pointer',
        overflow: 'hidden'
      }}
    >
      {/* Overweight Order Color Bar */}
      {shouldShowOverweightOrderUI() && (
        <div style={{
          width: 241,
          height: 8,
          backgroundColor: linkedOrdersForOverweightOrders?.color || '#875BF7',
          borderRadius: 100,
          position: 'absolute',
          top: 8,
          left: 8
        }} />
      )}
      
      {/* Content with padding */}
      <div style={{ padding: '8px', paddingTop: shouldShowOverweightOrderUI() ? '24px' : '8px', position: 'relative' }}>
        {/* Top-right badges container - Landing Zone and Time */}
        <div style={{
          position: 'absolute',
          top: shouldShowOverweightOrderUI() ? 24 : 8,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          zIndex: 1
        }}>
          {/* Landing Zone Badge */}
          {props.order.landingZoneName && (
            <Tooltip title={props.order.landingZoneName} arrow>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#FFF',
                borderRadius: 5,
                border: '1px solid #D9DBE9',
                padding: '4px 8px',
                cursor: 'help',
                minWidth: 40
              }}>
                <span style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#14142B',
                  whiteSpace: 'nowrap'
                }}>
                  {props.order.landingZoneName.substring(0, 3)}
                </span>
              </div>
            </Tooltip>
          )}

          {/* Time Badge - Performance optimized component that subscribes to time ticker */}
          <TimeUntilTakeoffBadge order={props.order} />
        </div>

        {/* Header Row - Logo and Order ID/Status */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
        {/* Merchant Logo */}
        <Tooltip title={props.order.outletName || props.order.externalProviderName || 'Merchant'} arrow>
          <div style={{ width: 40, height: 40, border: '1px solid #D9DBE9', borderRadius: 8, overflow: 'hidden', flexShrink: 0, cursor: 'help' }}>
            {pos?.logo ? (
                    <img
                      alt="store logo"
                      src={pos.logo}
                      style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
                ) : null}
          </div>
        </Tooltip>

        {/* Order Short ID and Status Title */}
        <div style={{ 
          flex: 1, 
          marginLeft: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Typography 
            variant="h5" 
            style={{ 
              fontSize: 18,
              fontWeight: 500,
              color: '#14142B',
              lineHeight: 1.2
            }}
          >
            {getShortIdForOrder(props.order)}
          </Typography>
          {props.order.originalOrderId && !inactiveStatuses.includes(props.order.status) && (
            <Typography 
              variant="body2" 
              style={{ 
                fontSize: 12,
                fontWeight: 500,
                color: '#EF4444',
                lineHeight: 1.2,
                marginTop: 2
              }}
            >
              Resending
            </Typography>
          )}
          <Typography 
            variant="body2" 
            style={{ 
              fontSize: 12,
              fontWeight: 700,
              color: '#14142B',
              lineHeight: 1.2,
              marginTop: 2
            }}
          >
            {activeMissionStatus && (
              <>
                <span style={{ color: activeMissionStatus.color, fontWeight: 600 }}>
                  {activeMissionStatus.text}
                </span>
              </>
            )}
          </Typography>
        </div>
      </div>

        {/* Status Component Area */}
        <OrderCardStatusIndicator 
          order={props.order}
          onOpenRouteConfirmation={(order) => {
            selectOrder();
            setTimeout(() => props.onOpenRouteConfirmation?.(order), 0);
          }}
          openSuggestModal={() => {
            selectOrder();
            setTimeout(() => props.openSuggestModal?.(), 0);
          }}
          onAssignPad={(e: React.MouseEvent) => {
            selectOrder();
            // Open menu anchored to bottom of card (same as PadSelectorBar)
            setTimeout(() => {
              if (padAnchorRef.current) {
                // Create synthetic event with anchor element and trigger the popup
                const syntheticEvent = {
                  ...e,
                  currentTarget: padAnchorRef.current,
                  target: padAnchorRef.current,
                  stopPropagation: () => {}
                } as React.MouseEvent<HTMLDivElement>;
                bindTrigger(popupStateAircraft).onClick(syntheticEvent);
              }
            }, 0);
          }}
          onAcceptOrder={(order) => {
            props.onAcceptOrder?.(order);
          }}
          linkedOrdersForOverweightOrders={linkedOrdersForOverweightOrders}
        />
      </div>

      {/* Invisible anchor point for programmatic pad menu opening - always present */}
      <div 
        ref={padAnchorRef}
        {...bindTrigger(popupStateAircraft)}
        onClick={(e) => e.stopPropagation()}
        style={{ 
          height: 0, 
          width: 0,
          position: 'absolute',
          bottom: 0,
          left: '50%'
        }}
      />

      {/* Pad Section - Bottom of card (show when selected, has pad assigned, showPadUI is enabled, or in specific statuses) */}
      {(isSelected ||
        showPadUI ||
        (!inactiveStatuses.includes(props.order.status) && props.order.originLandingPadId) ||
        props.order.status === 'RECEIVED_FROM_STORE' || 
        props.order.status === 'READY' ||
        props.order.status === 'DISPATCHED' ||
        props.order.status === 'DELIVERED') && (
        <PadSelectorBar
          order={props.order}
          popupStateAircraft={popupStateAircraft}
          popupStateArrivalPad={popupStateArrivalPad}
          getDeparturePadButtonText={getDeparturePadButtonText}
          getArrivalPadButtonText={getArrivalPadButtonText}
          shouldEnableArrivalPadTag={shouldEnableArrivalPadTag}
        />
      )}

      {/* Context Menu - Right-click menu */}
      <Menu {...bindMenu(popupStateContextMenu)} onClose={stop(bindMenu(popupStateContextMenu).onClose)}>
        <MenuItem 
          onClick={(e) => {
            e.stopPropagation();
            setShowPadUI(!showPadUI);
            popupStateContextMenu.close();
          }}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <Typography variant="body1" style={{ fontSize: 14 }}>
            {showPadUI ? '✓' : ''} Show pad UI
          </Typography>
        </MenuItem>
      </Menu>

      {/* Context Menus - Available at all times for pad assignment */}
      <Menu {...bindMenu(popupStateAircraft)} onClose={stop(bindMenu(popupStateAircraft).onClose)}>
        <OrderCardContext 
          popupState={popupStateAircraft} 
          order={props.order} 
          tags={props.order.tags} 
          subMenu="aircraft"
          aircraftData={props.aircraftData}
          pads={getAllActivePadsForOrder(props.order, props.pads || [])}
        />
      </Menu>
      
      <Menu {...bindMenu(popupStateArrivalPad)} onClose={stop(bindMenu(popupStateArrivalPad).onClose)}>
        <OrderCardContext 
          popupState={popupStateArrivalPad} 
          order={props.order} 
          tags={props.order.tags} 
          subMenu="arrival-pad"
          pads={getAllActivePadsForOrder(props.order, props.pads || [])}
        />
      </Menu>
    </div>
  );
};

export const OrderCard = memo(OrderCardComponent, areOrdersEqual);

